import styles from '../../styles/Registration.module.scss';
import { useState } from "react";
import { GeneralContext } from '../../contexts/GeneralContext';
import { LocaleContext } from '../../contexts/LocaleContext';
import { useContext, useEffect } from "react";
import { useNavigate, useParams } from 'react-router-dom';
import { globalRegex, getEmailRegexError } from '../../utils/utils';
import useMediaQuery from '@mui/material/useMediaQuery';
import ModalPortal from "../portal/modal";
import { Button } from 'react-bootstrap';
import axios from 'axios';
import { TextField } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';

const SUBMIT_STATES = {
    IDLE: 'idle',
    PENDING: 'pending',
    SUCCESS: 'success',
    ERROR: 'error',
};

const MAX_LENGTH_LASTNAME = 100;
const MAX_LENGTH_FIRSTNAME = 100;
const MIN_AGE = 18;
const MAX_AGE = 200;

const ENDPOINTS = Object.freeze({
    DEFAULT_REGISTER: "/users-permissions/register",
    PHYTO_REGISTER: "/users-permissions/registerPhytoUser",
    PHENOTYPAGE_REGISTER: "/users-permissions/registerPhenotypage",
    TMO_PATIENT_REGISTER: "/users-permissions/registerTMOPatient",
});

const FORM_IMAGE = Object.freeze({
    DEFAULT_IMAGE: { src: "/images/Diabeteformulaire.png", alt: "Créer mon compte Prévention diabète de type 2" },
    PHENOTYPAGE_IMAGE: { src: "/images/Phenoformulaire.png", alt: "Phénotypage de l'obésité" },
    PHYTO_IMAGE: { src: "/images/PhytoAccompagnementformulaire.png", alt: "Maladies & pesticides : Comment accompagner ?" },
    TMO_PATIENT_IMAGE: { src: "/images/tmo_formulaire.png", alt: "Médicaments & Obésité" },
});

const THEMATIC_CONTENT_TYPE = Object.freeze({
    EETP: "e-etp",
    EPREVENTION: "e-prevention",
    EFORMATION: "e-formation",
});

const REGISTRATION_RULES = [
    { match: (ctx) => ctx.isPhytoUser, type: "phyto", endpoint: ENDPOINTS.PHYTO_REGISTER, image: FORM_IMAGE.PHYTO_IMAGE, contentType: THEMATIC_CONTENT_TYPE.EFORMATION},
    { match: (ctx) => ctx.isPhenotypageUser, type: "pheno", endpoint: ENDPOINTS.PHENOTYPAGE_REGISTER, image: FORM_IMAGE.PHENOTYPAGE_IMAGE, contentType: THEMATIC_CONTENT_TYPE.EFORMATION },
    { match: (ctx) => ctx.isTMOPatient, type: "tmo patient", endpoint: ENDPOINTS.TMO_PATIENT_REGISTER, image: FORM_IMAGE.TMO_PATIENT_IMAGE, contentType: THEMATIC_CONTENT_TYPE.EETP },
];

const Registration = () => {
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width: 760px)');
    const { thematic: thematic_param } = useParams();
    let formData = {};

    const { locale, dateOfBirthPlaceholder } = useContext(LocaleContext);
    const [dateOfBirth, setDateOfBirth] = useState(null);
    const [thematic, setThematic] = useState();
    const [currentEmail, setEmail] = useState("");
    const [proTypes, setProTypes] = useState([]);
    const [proSpecialities, setProSpecialities] = useState([]);
    const [isProCertified, setIsProCertified] = useState(false);
    const [isProCertifiedClicked, setIsProCertifiedClicked] = useState(false);
    const [isProPhytoAllowData, setIsProPhytoAllowData] = useState(false);
    const [isProPhytoAllowDataClicked, setIsProPhytoAllowDataClicked] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(null);
    const [submitState, setSubmitState] = useState(SUBMIT_STATES.IDLE);
    const [formError, setFormError] = useState("");
    const [errors, setErrors] = useState({
        lastname: "",
        firstname: "",
        profession: "",
        speciality: "",
        zipcode: "",
        dateofbirth: "",
        email: ""
    });

    const generalDetails = useContext(GeneralContext);

    // Thematic's name and boolean parameter from additional('form_thematic') thematic data in strapi DB.
    const PHENO_THEMATIC = "recommandations_has";
    const PHYTO_THEMATIC = "maladie_et_pesticides";
    const TMO_PATIENT_THEMATIC = "médicaments_obésité_patient";

    //Managing user type based on page theme
    const isPhenotypage = !!thematic?.form_thematic?.phenotypage;
    const isPhytoUser = !!thematic?.form_thematic?.[PHYTO_THEMATIC];
    const isTMOPatient = thematic?.name === TMO_PATIENT_THEMATIC;

    //Managing the form image on the page based on the theme, by returning a 'formImage' via JSX
    const context = {
        isPhenotypageUser: isPhenotypage && !isPhytoUser && thematic?.name?.includes(PHENO_THEMATIC),
        isPhytoUser: isPhytoUser && thematic?.name?.includes(PHYTO_THEMATIC),
        isTMOPatient: isTMOPatient && thematic?.name?.includes(TMO_PATIENT_THEMATIC),
    }

    const { image: formImage } = REGISTRATION_RULES.find((rule) => rule.match(context)) || { image: FORM_IMAGE.DEFAULT_IMAGE };

    useEffect(() => {
        setIsProCertified(!isPhenotypage && !isPhytoUser);
    }, [isPhenotypage, isPhytoUser]);

    const onEmailChange = (event) => {
        if (submitState === SUBMIT_STATES.SUCCESS) setSubmitState(SUBMIT_STATES.IDLE);
        setEmail(event.target.value);
    }

    useEffect(() => {
        if (submitState === SUBMIT_STATES.ERROR) {
            setTimeout(() => {
                setSubmitState(SUBMIT_STATES.IDLE);
            }, 10000);
        }
    }, [submitState]);

    useEffect(() => {
        axios.get(process.env.REACT_APP_NUVEE_BASE_URL + `/users-permissions/publicAccountRegister/${thematic_param}`)
            .then(response => {
                setProTypes(response.data.protypes);
                setThematic(response.data.thematic);
            })
            .catch((error) => {
                console.error(error);
                setShowConfirmationModal({ type: "ERROR_FETCHING_DATA" });
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [thematic_param]);


    const handleSubmit = async () => {
        try {
            setSubmitState(SUBMIT_STATES.PENDING);
            generalDetails.setShowSpinner(true);
            formData = {};
            let isFormValid = true;

            // Data validation
            const inputs = Array.from(document.getElementsByTagName('input'));
            isFormValid = await validateInputFields(inputs) && isFormValid;

            if (isPhenotypage || isPhytoUser) {
                const selects = Array.from(document.getElementsByTagName('select'));
                isFormValid = validateSelectFields(selects) && isFormValid;
            }

            if (isPhenotypage || isPhytoUser) {
                setIsProCertifiedClicked(true);
                if (!isProCertified) isFormValid = false;
            }
            if (isPhytoUser) {
                setIsProPhytoAllowDataClicked(true);
                if (!isProPhytoAllowData) isFormValid = false;
            }
            if (!isFormValid) throw new Error("Certains champs ne sont pas valides");

            // Prepare and send user data
            const userData = prepareUserData(formData);
            const result = await submitUserData(userData);

            // From res, we can determine the type of response. Codes are set in backend @ User utils
            // the 'type' in 'setShowConfirmationModal' state is used to display corresponding modal and content
            switch (result?.data) {
                // Partie PHYTO / Maladie et pesticides
                case "RES_REGISTER_PHYTO_CREATED":
                    setSubmitState(SUBMIT_STATES.SUCCESS);
                    setShowConfirmationModal({ type: "PHYTO_CREATED" });
                    setFormError("");
                    break;

                case "RES_REGISTER_PHYTO_ADDED":
                case "RES_REGISTER_PHYTO_ALREADY_ADDED":
                    setSubmitState(SUBMIT_STATES.SUCCESS);
                    setShowConfirmationModal({ type: "PHYTO_ADDED" });
                    setFormError("");
                    break;

                case "RES_REGISTER_PHYTO_ERROR_ROLE":
                    throw new Error(
                        "Votre compte ne peut pas être créé, nous vous invitons à contacter notre équipe à l'adresse suivante support@nuvee.fr afin que nous puissions vous accompagner."
                    );

                case "RES_REGISTER_PHYTO_ERROR":
                    throw new Error(
                        "Une erreur est survenue lors de l'inscription au parcours Maladie et pesticide."
                    );


                // Partie Phenotypage / Has Recommandations
                case "RES_REGISTER_PHENO_CREATED":
                case "RES_REGISTER_CREATED":
                case "RES_REGISTER_TMO_CREATED":
                    setSubmitState(SUBMIT_STATES.SUCCESS);
                    setShowConfirmationModal({ type: "CREATED" });
                    setFormError("");
                    break;

                case "RES_REGISTER_PHENO_ADDED":
                    setSubmitState(SUBMIT_STATES.SUCCESS);
                    setShowConfirmationModal({ type: "PHENO_ADDED" });
                    setFormError("");
                    break;

                case "RES_REGISTER_ADDED":
                case "RES_REGISTER_TMO_ADDED":
                case "RES_REGISTER_TMO_ALREADY_ADDED":
                    setSubmitState(SUBMIT_STATES.SUCCESS);
                    setShowConfirmationModal({ type: "ADDED" });
                    setFormError("");
                    break;

                case "RES_REGISTER_ERROR_ROLE":
                case "RES_REGISTER_ERROR_THEMATIC_EXISTS":
                case "RES_REGISTER_PHENO_ERROR_ROLE":
                    throw new Error(
                        "Une erreur est survenue lors de la création de votre compte : l'adresse mail renseignée n'est pas exacte ou est déjà utilisée sur Nuvee. Pour plus d'informations, veuillez contacter le support à support@nuvee.fr."
                    );
                case "RES_REGISTER_PHENO_ERROR":
                case "RES_REGISTER_ERROR":
                case "RES_REGISTER_TMO_ERROR":
                default:
                    throw new Error(
                        "Une erreur est survenue, merci de réessayer plus tard. Si l'erreur persiste, veuillez contacter le support à support@nuvee.fr."
                    );
            }
        } catch (error) {
            console.error("Erreur handleSubmit:", error);
            setFormError(error.message);
            setSubmitState(SUBMIT_STATES.ERROR);
        } finally {
            generalDetails.setShowSpinner(false);
        }
    };

    useEffect(() => {
        const isNoError = Object.values(errors).every(error => error === "");
        if (isNoError) {
            setFormError("");
        } else {
            setFormError("Certains champs ne sont pas valides");
        }
    }, [errors]);

    /*
     Fields validations
    */
    const handleLastnameError = (value) => {
        let isValid = false;
        const lastNameMatch = value.match(globalRegex.stringRegex);

        if (value.length === 0) {
            setErrors(prevErrors => ({ ...prevErrors, lastname: "Veuillez renseigner un nom de famille" }));
        } else if (value.length > MAX_LENGTH_LASTNAME) {
            setErrors(prevErrors => ({ ...prevErrors, lastname: `Le nom de famille ne doit pas dépasser ${MAX_LENGTH_LASTNAME} caractères` }));
        } else if (lastNameMatch === null) {
            setErrors(prevErrors => ({ ...prevErrors, lastname: "Veuillez renseigner un nom de famille valide" }));
        } else {
            setErrors(prevErrors => ({ ...prevErrors, lastname: "" }));
            formData.lastname = value;
            isValid = true;
        }

        return isValid;
    };

    const handleFirstnameError = (value) => {
        let isValid = false;
        const firstNameMatch = value.match(globalRegex.stringRegex);

        if (value.length === 0) {
            setErrors(prevErrors => ({ ...prevErrors, firstname: "Veuillez renseigner un prénom" }));
        } else if (value.length > MAX_LENGTH_FIRSTNAME) {
            setErrors(prevErrors => ({ ...prevErrors, firstname: `Le prénom ne doit pas dépasser ${MAX_LENGTH_FIRSTNAME} caractères` }));
        } else if (firstNameMatch === null) {
            setErrors(prevErrors => ({ ...prevErrors, firstname: "Veuillez renseigner un prénom valide" }));
        } else {
            setErrors(prevErrors => ({ ...prevErrors, firstname: "" }));
            formData.firstname = value;
            isValid = true;
        }

        return isValid;
    };

    const handleZipcodeError = (value) => {
        let isValid = false;
        const zipcodeMatch = value.match(globalRegex.zipcodeRegex);

        if (value.length === 0) {
            setErrors(prevErrors => ({ ...prevErrors, zipcode: "Veuillez renseigner un code postal" }));
        } else if (zipcodeMatch === null) {
            setErrors(prevErrors => ({ ...prevErrors, zipcode: "Veuillez renseigner un code postal valide" }));
        } else {
            setErrors(prevErrors => ({ ...prevErrors, zipcode: "" }));
            formData.zipcode = value;
            isValid = true;
        }

        return isValid;
    };

    const handleDateofbirthError = (date) => {
        let isValid = false;

        if (!date || date.length === 0) {
            setErrors(prevErrors => ({ ...prevErrors, dateofbirth: "Veuillez renseigner une date de naissance" }));
        } else if (isNaN(date.getTime())) {
            setErrors(prevErrors => ({ ...prevErrors, dateofbirth: "Le format de la date est incorrect" }));
        } else {
            const today = new Date();
            const minDate = new Date(today);
            const maxDate = new Date(today);
            minDate.setFullYear(today.getFullYear() - MAX_AGE);
            maxDate.setFullYear(today.getFullYear() - MIN_AGE);

            if (date < minDate) {
                setErrors(prevErrors => ({ ...prevErrors, dateofbirth: "Le format de la date est incorrect" }));
            } else if (date > maxDate) {
                setErrors(prevErrors => ({ ...prevErrors, dateofbirth: "Vous devez avoir au moins 18 ans" }));
            } else {
                setErrors(prevErrors => ({ ...prevErrors, dateofbirth: "" }));
                formData.dateofbirth = date;
                isValid = true;
            }
        }

        return isValid;
    };

    const handleEmailError = async (value) => {
        if (value.length === 0) {
            setErrors(prevErrors => ({ ...prevErrors, email: "Veuillez renseigner une adresse mail" }));
            return false;
        }
        const emailRegexError = getEmailRegexError(value);
        if (emailRegexError) {
            setErrors(prevErrors => ({ ...prevErrors, email: emailRegexError }));
            return false;
        }
        setErrors(prevErrors => ({ ...prevErrors, email: "" }));
        formData.email = value;
        return true;
    };

    const validateInputFields = async (inputs) => {
        let isValid = true;

        for (const element of inputs) {
            const value = element.value;

            switch (element.name) {
                case 'lastname':
                    isValid = handleLastnameError(value) && isValid;
                    break;
                case 'firstname':
                    isValid = handleFirstnameError(value) && isValid;
                    break;
                case 'zipcode':
                    isValid = handleZipcodeError(value) && isValid;
                    break;
                case 'dateofbirth': // ici données non nécessaires pour phenotypage ou phytoUser
                    if (!isPhenotypage && !isPhytoUser) {
                        isValid = handleDateofbirthError(dateOfBirth) && isValid;
                    }
                    break;
                case 'email':
                    isValid = await handleEmailError(value) && isValid;
                    break;
                default:
                    break;
            }
        }

        return isValid;
    };

    const handleProfessionError = (value) => {
        let isValid = false;

        if (value <= 0) {
            setErrors(prevErrors => ({ ...prevErrors, profession: "Veuillez sélectionner une profession valide." }));
        } else {
            setErrors(prevErrors => ({ ...prevErrors, profession: "" }));
            formData.profession = value;
            isValid = true;
        }

        return isValid;
    };

    const handleSpecialityError = (value) => {
        let isValid = false;

        if (value <= 0) {
            setErrors(prevErrors => ({ ...prevErrors, speciality: "Veuillez sélectionner une spécialité valide." }));
        } else {
            setErrors(prevErrors => ({ ...prevErrors, speciality: "" }));
            formData.speciality = value;
            isValid = true;
        }

        return isValid;
    };


    const validateSelectFields = (selects) => {
        let isValid = true;

        selects.forEach(element => {
            const value = parseInt(element.value);

            switch (element.name) {
                case 'profession':
                    isValid = handleProfessionError(value) && isValid;
                    break;
                case 'speciality':
                    isValid = handleSpecialityError(value) && isValid;
                    break;
                default:
                    break;
            }
        });

        return isValid;
    };

    const prepareUserData = (formData) => {
        if (!thematic) return null;
        const { contentType } = REGISTRATION_RULES.find((rule) => rule.match(context)) || { contentType: THEMATIC_CONTENT_TYPE.EPREVENTION };
        const userData = {
          firstname: formData.firstname,
          lastname: formData.lastname,
          email: formData.email,
          confirmed: false,
          userThematics: thematic.contents
            .filter((content) => content.thematicType?.diplome === contentType)
            .map((content) => ({
              thematics: [thematic.id],
              thematicType: content.thematicType?.id,
            })),
          isNuvee: false,
          Contact: {
            postalCode: formData.zipcode,
          },
        };

        if (isPhenotypage) {
            userData.Additional_Information = [{
                __component: "additionalinformation.professional",
                speciality: { id: formData.speciality },
                professional_type: { id: formData.profession },
                phenotypage: true,
                eFormation: true,
            }];
        }

        // PHYTO USER / Maladies et pesticides
        else if (isPhytoUser) {
            userData.Agreements = {proPhytoAllowData: isProPhytoAllowData}
            userData.Additional_Information = [{
                __component: "additionalinformation.professional",
                speciality: { id: formData.speciality },
                professional_type: { id: formData.profession },
                eFormation: true,
            }];
        }

        // Cas d'un PATIENT classique
        else {
            userData.Additional_Information = [{
                __component: "additionalinformation.patient",
                dateOfBirth: format(formData.dateofbirth, 'yyyy-MM-dd'),
            }];
        }

        return userData;
    };

    const submitUserData = async (userData) => {
        const { endpoint = ENDPOINTS.DEFAULT_REGISTER, type: userType = "prevention" } = REGISTRATION_RULES.find((rule) => rule.match(context)) || {};
        try {
            const payload = { data: userData };
            return await axios.post(process.env.REACT_APP_NUVEE_BASE_URL + endpoint, payload);
        } catch (error) {
            // Unexpected error. log error and let parent handle user error
            console.error(`Error while registering (${userType}):`, error);
            return null;
        }
    };

    const handleProtypeChange = (proTypeId) => {
        if (proTypeId) {
            let data = proTypes.find((pt) => pt.id === Number(proTypeId));
            if (data) {
                setProSpecialities(data.specialities);
            } else {
                setProSpecialities([]);
            }
        }
    }

    return (
        <>
            {
                thematic && (
                    <>
                        <div className={styles.container}>
                            <main className={styles.main}>
                                <h1 className={styles.title}>
                                    {thematic?.form_thematic?.title || `Créer mon compte ${thematic?.longTitle}`}
                                </h1>
                                {thematic?.form_thematic?.description && <p className={styles.description}>{thematic.form_thematic.description}</p>}
                                <div className={styles.containerFormDiabete}>
                                    <img src={formImage?.src} alt={formImage?.alt} />
                                    <div className={styles.subContainerFormDiabete}>
                                        <div className={styles.patientForm}>
                                            <span className={styles.allFieldRequired}>
                                                * Tous les champs sont obligatoires
                                            </span>
                                            <h4>
                                                VOS COORDONNÉES
                                                <div style={thematic?.theme?.color && thematic?.name.indexOf("diabete") === -1 ? { borderBottomColor: thematic.theme.color } : {}}></div>
                                            </h4>
                                            <div className={styles.patientForm}>
                                                <div className={styles.formContainer}>
                                                    <div className={styles.boxFlex}>
                                                        <label>Nom</label>
                                                        <input type="text" className={errors.lastname ? styles.inputko : styles.inputok} name="lastname" onChange={(e) => handleLastnameError(e.target.value)} />
                                                        <span className={styles.error}>{errors.lastname}</span>
                                                    </div>
                                                    <div className={styles.boxFlex} style={{ marginLeft: `${!isMobile ? "1em" : '0'}` }}>
                                                        <label>Pr&eacute;nom</label>
                                                        <input type="text" className={errors.firstname ? styles.inputko : styles.inputok} name="firstname" onChange={(e) => handleFirstnameError(e.target.value)} />
                                                        <span className={styles.error}>{errors.firstname}</span>
                                                    </div>
                                                </div>
                                                {(isPhenotypage || isPhytoUser) &&
                                                    <>
                                                        <div className={`${styles.formContainer} ${styles.dropdownRow}`}>
                                                            {proTypes.length > 0 &&
                                                                <div className={`${styles.boxFlex} ${styles.selectLabel}`}>
                                                                    <label>Profession</label>
                                                                    <select
                                                                        className={errors.profession ? styles.inputko : styles.inputok}
                                                                        name="profession"
                                                                        onChange={(e) => {
                                                                            handleProtypeChange(e.target.value);
                                                                            handleProfessionError(e.target.value);
                                                                        }}
                                                                    >
                                                                        <option value={0}>Sélectionnez une profession</option>
                                                                        {proTypes.map(pro => {
                                                                            return <option key={pro.id} value={pro.id}>{pro.name}</option>
                                                                        })}
                                                                    </select>
                                                                    <span className={styles.error}>{errors.profession}</span>
                                                                </div>
                                                            }
                                                            {proSpecialities.length > 0 &&
                                                                <div className={`${styles.boxFlex} ${styles.selectLabel}`}>
                                                                    <label>Spécialité</label>
                                                                    <select
                                                                        className={errors.speciality ? styles.inputko : styles.inputok}
                                                                        name="speciality"
                                                                        onChange={(e) => handleSpecialityError(e.target.value)}
                                                                    >
                                                                        <option value={0}>Sélectionnez une spécialité</option>
                                                                        {proSpecialities.map(spe => (
                                                                            <option key={spe.id} value={spe.id}>{spe.name}</option>
                                                                        ))}
                                                                    </select>
                                                                    <span className={styles.error}>{errors.speciality}</span>
                                                                </div>
                                                            }
                                                        </div>
                                                    </>
                                                }
                                                <div className={styles.formContainer}>
                                                    <div className={styles.boxFlex}>
                                                        <label>Code postal</label>
                                                        <input type="text" className={errors.zipcode ? styles.inputko : styles.inputok} name="zipcode" onChange={(e) => handleZipcodeError(e.target.value)} />
                                                        <span className={styles.error}>{errors.zipcode}</span>
                                                    </div>
                                                    {(!isPhenotypage && !isPhytoUser) &&
                                                        <div className={styles.boxFlex} style={{ marginLeft: `${!isMobile ? "1em" : '0'}` }}>
                                                            <label>Date de naissance</label>
                                                            <LocalizationProvider dateAdapter={AdapterDateFns} locale={locale}>
                                                                <DatePicker
                                                                    value={dateOfBirth}
                                                                    onChange={(newDate) => {
                                                                        handleDateofbirthError(newDate);
                                                                        setDateOfBirth(newDate);
                                                                    }}
                                                                    renderInput={(params) => (
                                                                        <TextField
                                                                            {...params}
                                                                            name="dateofbirth"
                                                                            error={!!errors.dateofbirth}
                                                                            fullWidth
                                                                            variant="standard"
                                                                            className={errors.dateofbirth ? 'inputko' : 'inputok'}
                                                                            inputProps={{
                                                                                ...params.inputProps,
                                                                                placeholder: dateOfBirthPlaceholder,
                                                                            }}
                                                                        />
                                                                    )}
                                                                />
                                                            </LocalizationProvider>
                                                            <span className={styles.error}>{errors.dateofbirth}</span>
                                                        </div>
                                                    }
                                                </div>
                                                <div className={styles.formContainer}>
                                                    <div className={styles.boxFlex}>
                                                        <label>Adresse mail</label>
                                                        <input type="text" className={errors.email ? styles.inputko : styles.inputok} name="email" onChange={(e) => { onEmailChange(e); handleEmailError(e.target.value); }} />
                                                        <span className={styles.error}>{errors.email}</span>
                                                    </div>
                                                </div>
                                            {(isPhenotypage || isPhytoUser) && (
                                                <div>
                                                    <div className={`${styles.cguConditions} ${(isProCertifiedClicked && !isProCertified) ? styles.cguConditionsError : ""}`}>
                                                        <label>
                                                            <input type="checkbox" name="proCertifiedCgu"
                                                                onChange={(e) => {
                                                                    setIsProCertifiedClicked(true);
                                                                    setIsProCertified(e.target.checked)
                                                                }}
                                                            />
                                                            <span>En cochant cette case, je certifie sur l'honneur que je suis un professionnel de santé et que les informations communiquées sont exactes.</span>
                                                        </label>
                                                    </div>
                                                    {isPhytoUser &&
                                                        <div className={`${styles.cguConditions} ${(isProPhytoAllowDataClicked && !isProPhytoAllowData) ? styles.cguConditionsError : ""}`}>
                                                            <label>
                                                                <input type="checkbox" name="proPhytoAllowDataCgu"
                                                                    onChange={(e) => {
                                                                        setIsProPhytoAllowDataClicked(true);
                                                                        setIsProPhytoAllowData(e.target.checked)
                                                                    }}
                                                                />
                                                                <span>J'autorise l'association Phyto-Victimes à utiliser mes données à des fins de communication.</span>
                                                            </label>
                                                        </div>
                                                    }
                                                </div>
                                            )}
                                            </div>

                                            <div className={styles.send}>
                                                <button
                                                    disabled={!isProCertified || (isPhytoUser && !isProPhytoAllowData) || submitState === SUBMIT_STATES.PENDING || submitState === SUBMIT_STATES.SUCCESS || formError}
                                                    style={thematic?.theme?.color && thematic?.name.indexOf("diabete") === -1 ? { background: thematic.theme.color } : {}}
                                                    onClick={handleSubmit}
                                                >
                                                    {(submitState === SUBMIT_STATES.IDLE || submitState === SUBMIT_STATES.ERROR) && <>CR&Eacute;ER MON COMPTE</>}
                                                    {submitState === SUBMIT_STATES.PENDING && <>CR&Eacute;ATION EN COURS...</>}
                                                    {submitState === SUBMIT_STATES.SUCCESS && <>ENREGISTREMENT R&Eacute;USSI</>}
                                                </button>
                                                {formError &&
                                                    <div className={styles.sendError}>
                                                        {formError}
                                                    </div>
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </main>
                        </div>
                    </>
                )
            }
            {/* Modal for links */}
            <ModalPortal
                handleClose={() => setShowConfirmationModal(null)}
                title={""}
                show={!!showConfirmationModal}
                footerModal={
                    <Button
                        variant={"primary"}
                        onClick={() => {
                            setShowConfirmationModal(null);

                            if (
                                showConfirmationModal?.type === "ADDED" ||
                                showConfirmationModal?.type === "PHENO_ADDED" ||
                                showConfirmationModal?.type === "PHYTO_ADDED" ||
                                showConfirmationModal?.type === "ERROR_FETCHING_DATA" ||
                                showConfirmationModal?.type === "CREATED"
                            ) {
                                navigate("/login", { state: { email: currentEmail } });
                            }
                        }}
                    >
                        {(showConfirmationModal?.type === "CREATED" ||
                            showConfirmationModal?.type === "PHYTO_CREATED") && (
                                <>J'ai compris</>
                            )}

                        {(showConfirmationModal?.type === "ADDED" ||
                            showConfirmationModal?.type === "PHENO_ADDED" ||
                            showConfirmationModal?.type === "PHYTO_ADDED") && (
                                <>Me connecter à Nuvee</>
                            )}

                        {showConfirmationModal?.type === "ERROR_FETCHING_DATA" && (
                            <>Retour à l'accueil</>
                        )}
                    </Button>
                }
            >

                {/* CREATED / PHYTO_CREATED */}
                {(showConfirmationModal?.type === "CREATED" ||
                    showConfirmationModal?.type === "PHYTO_CREATED") && (
                        <>
                            <p>Merci pour votre inscription !</p>
                            <p>
                                Dans quelques instants, vous allez recevoir un lien par e-mail
                                permettant de finaliser l'activation de votre compte Nuvee.
                            </p>
                            <p>
                                Vous n'avez pas reçu notre e-mail ? Pensez à bien vérifier vos
                                spams et courriers indésirables. Si votre problème persiste,
                                n'hésitez pas à nous contacter à support@nuvee.fr.
                            </p>
                        </>
                    )}

                {/* PHENO_ADDED / PHYTO_ADDED */}
                {(showConfirmationModal?.type === "PHENO_ADDED" ||
                    showConfirmationModal?.type === "PHYTO_ADDED") && (
                        <>
                            <p>
                                Votre demande a bien été enregistrée. Si vous n'avez pas encore
                                de compte, vous recevrez un e-mail pour l'activer. Si votre
                                compte est déjà actif, les éléments demandés ont été ajoutés et
                                vous pouvez vous connecter dès maintenant.
                            </p>
                            <p>
                                En cas de besoin, vous pouvez nous contacter à
                                support@nuvee.fr.
                            </p>
                        </>
                    )}

                {/* ADDED */}
                {showConfirmationModal?.type === "ADDED" && (
                    <>
                        <p>
                            Votre demande a bien été prise en compte. Si vous n'avez pas
                            encore de compte, vous recevrez un e-mail pour l'activer. Si
                            votre compte est déjà actif, la thématique a été ajoutée et
                            vous pouvez vous connecter dès maintenant.
                        </p>
                        <p>
                            En cas de besoin, vous pouvez nous contacter à
                            support@nuvee.fr.
                        </p>
                    </>
                )}

                {/* Error if the thematic is not found */}
                {showConfirmationModal?.type === "ERROR_FETCHING_DATA" && (
                    <>
                        <p>
                            Une erreur est survenue lors du chargement de la page. Veuillez réessayer plus tard.
                        </p>
                        <p>
                            Si le problème persiste, n'hésitez pas à nous contacter à <a href="mailto:support@nuvee.fr">support@nuvee.fr</a>.
                        </p>
                    </>
                )}
            </ModalPortal>
        </>
    )
}

export default Registration;
